
import { defineComponent, computed, watch, reactive } from 'vue'
import { AxiosResponse } from 'axios'
import JrSelect from '@/components/jr-parts/JrSelect.vue'
import { Station } from '@/api'
import { ListRequest } from '@/requests/station'

/**
 * 駅一覧を取得する
 */
const getStations = async (lineCode?: string): Promise<Station[]> => {
  const request = lineCode != null ? new ListRequest(lineCode) : new ListRequest()
  await request.exec()
  if (request.success) {
    const { data } = request.getResponse() as AxiosResponse<Array<Station>>
    return data
  }
  return []
}

export default defineComponent({
  name: 'StationSelect',
  components: {
    JrSelect,
  },
  emits: ['update:modelValue', 'update:name'],
  props: {
    lineCode: {
      type: String,
      required: false,
      default: '',
    },
    modelValue: {
      type: Number,
      required: false,
      default: null,
    },
    blank: {
      type: Boolean,
      required: false,
      default: true,
    },
    popperAppendToBody: {
      type: Boolean,
      required: false,
      default: true,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    // 路線に紐づけるかどうか。紐づける場合、路線が変わったタイミングで読み込む
    filterByLine: {
      type: Boolean,
      required: false,
      default: false,
    },
    size: {
      type: String,
      required: false,
      default: 'medium',
    },
    isEdit: {
      type: Boolean,
      required: false,
      default: true,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  async setup(props, context) {
    const stations = reactive<Station[]>([])

    const update = (station_id: number) => {
      // 選択したvalueを返す
      context.emit('update:modelValue', station_id === -1 && props.nullable ? null : station_id)

      const selectedStation = stations.find((station) => station.station_id === station_id)
      if (selectedStation != null && station_id !== -1) {
        context.emit('update:name', selectedStation.station_name)
      } else {
        context.emit('update:name', '')
      }
    }

    // lineが変更されたら選択を初期化するwatch
    watch(
      () => props.lineCode,
      (newLineCode, oldLineCode) => {
        // 路線が未指定ではなく、路線との関連付けが有効の場合
        if (newLineCode !== '' && newLineCode != null && props.filterByLine) {
          // 既に対象路線の駅情報があるかを検索
          const index = stations.findIndex((station) => station.line === newLineCode)
          if (index < 0) {
            // 駅情報が無い場合、取得する
            getStations(newLineCode)
              .then((res) => {
                stations.push(...res)
              })
              .catch((err) => {
                throw err
              })
          }
        }
        if (props.filterByLine && oldLineCode !== '' && oldLineCode !== null) {
          // 前回の路線コードが未指定から指定に変わった場合以外は選択肢を初期化する
          // 要は最初から路線コードが決まっている場合の初期読み込み以外リセット
          update(-1)
        }
      }
    )

    if (props.blank) {
      // 空白の選択肢を付与する
      stations.push({
        station_id: -1,
        station_name: ' ',
        station_sort: -1,
        line: '',
      })
    }

    if (!props.filterByLine) {
      // 路線での絞り込み有効では無い場合、全取得する
      stations.push(...(await getStations()))
    } else if (props.lineCode !== '' && props.lineCode !== null) {
      // 初期で路線コードが設定されている場合、取得する
      stations.push(...(await getStations(props.lineCode)))
    }

    const filterdStation = computed(() => {
      // 路線に紐づけない場合かつ路線コード未指定時は全て、指定時はその路線を抽出する。空白の選択肢はあれば必ず付与する
      return stations.filter(
        (station) =>
          (!props.filterByLine && props.lineCode === '') || station.line === props.lineCode || station.line === ''
      )
    })
    return {
      filterdStation,
      update,
    }
  },
})
